import classNames from 'classnames';
import type { FC, MouseEvent, ReactNode } from 'react';
import { PopupCloseIcon } from 'src/components/shared/elements/icons/PopupCloseIcon';
import type { Tailwind } from 'styles/tw';

export type SlideModalCtaOption = {
  disabled?: boolean;
  onClick?: () => void;
  text?: string;
  bgClassName?: Tailwind['backgroundColor'];
  textClassName?: Tailwind['color'];
  buttonClassNames?: {
    width?: Tailwind['width'];
    height?: Tailwind['height'];
    maxWidth?: Tailwind['maxWidth'];
    borderColor?: Tailwind['borderColor'];
    borderWidth?: Tailwind['borderWidth'];
    borderStyle?: Tailwind['borderStyle'];
    borderRadius?: Tailwind['borderRadius'];
  };
};

type Props = {
  isOpen: boolean;
  overlayClassName?: string;
  containerClassName?: string;
  shouldCloseOnClick?: boolean;
  closeModal: () => void;
  children?: ReactNode;
  handleContentClick: (event: MouseEvent) => void;
  withDefaultLayout?: boolean;
  ctaOption?: SlideModalCtaOption;
};

export const Presenter: FC<Props> = ({
  withDefaultLayout = true,
  ctaOption,
  isOpen,
  overlayClassName,
  containerClassName,
  closeModal,
  shouldCloseOnClick = false,
  children,
  handleContentClick
}) => {
  return (
    <div
      className={classNames(
        'fixed inset-0 bg-gray300 flex items-end justify-center z-[1000]',
        isOpen ? 'bg-opacity-70' : 'bg-opacity-0',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
        overlayClassName
      )}
      onClick={closeModal}
      data-testid="slide-modal-overlay"
    >
      <div
        className={classNames(
          'relative bg-white rounded-t-lg',
          'transform transition-transform duration-300',
          isOpen ? 'translate-y-0' : 'translate-y-[200%]',
          containerClassName
        )}
        onClick={handleContentClick}
      >
        <button
          className={classNames(
            'absolute -top-14 right-4',
            'text-gray-700 bg-white w-10 h-10 rounded-full',
            'flex items-center justify-center'
          )}
          onClick={closeModal}
        >
          <PopupCloseIcon />
        </button>
        {withDefaultLayout ? (
          <div className="pt-10 px-4 pb-[60px] flex flex-col max-h-[90vh]">
            <div className="max-h-[75vh] overflow-y-auto hidden-scrollbar">{children}</div>
            {ctaOption && (
              <div className="flex justify-center mt-8 shrink-0">
                <button
                  className={classNames(
                    'py-2 px-8 font-medium rounded-3xl',
                    ctaOption?.bgClassName,
                    ctaOption?.textClassName,
                    ctaOption?.buttonClassNames?.width,
                    ctaOption?.buttonClassNames?.height,
                    ctaOption?.buttonClassNames?.maxWidth,
                    ctaOption?.buttonClassNames?.borderRadius,
                    ctaOption?.buttonClassNames?.borderColor,
                    ctaOption?.buttonClassNames?.borderWidth,
                    ctaOption?.buttonClassNames?.borderStyle,
                    {
                      'opacity-60': ctaOption?.disabled
                    }
                  )}
                  disabled={ctaOption?.disabled}
                  onClick={shouldCloseOnClick ? closeModal : ctaOption?.onClick}
                  data-testid="slide-modal-cta"
                >
                  {ctaOption?.text}
                </button>
              </div>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
